import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { getOrderByToken, confirmOrder, sendOrderMessage, submitOrderQuote, completeOrder, Order, Issue } from '../lib/api';
import { 
  CheckCircle,
  XCircle,
  Wrench,
  File,
  PaperPlaneTilt,
  Receipt,
  ChatText
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileUpload } from '../components/FileUpload';
import { toast } from 'sonner';

interface EmailMessage {
  body?: string;
  direction?: 'inbound' | 'outbound';
  from_email?: string;
  created_at: string;
}

interface OrderData {
  order: Order & { email_thread?: { messages?: EmailMessage[] } };
  issue: Issue;
}

export default function OrderConfirmPage() {
  const { t, i18n } = useTranslation();
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  
  const [data, setData] = useState<OrderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'order' | 'quote' | 'complete'>('order');
  const [actionPending, setActionPending] = useState<'accept' | 'reject' | null>(null);
  
  // Message form
  const [messageText, setMessageText] = useState('');
  
  // Quote form
  const [quoteForm, setQuoteForm] = useState<{ amount: string; description: string; attachments: string[] }>({
    amount: '',
    description: '',
    attachments: []
  });
  
  // Completion form
  const [completionForm, setCompletionForm] = useState<{ notes: string; attachments: string[] }>({
    notes: '',
    attachments: []
  });

  useEffect(() => {
    const fetchOrder = async (): Promise<void> => {
      if (!token) return;
      try {
        const { data: orderData } = await getOrderByToken(token);
        const orderWithIssue = orderData as Order & { issue?: Issue };
        setData({
          order: orderWithIssue,
          issue: orderWithIssue?.issue || {} as Issue
        });
        
        const action = searchParams.get('action');
        if (action && orderData?.status === 'pending' && (action === 'accept' || action === 'reject')) {
          setActionPending(action);
        }
      } catch (error) {
        console.error('Failed to fetch order:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [token, searchParams]);

  const handleAccept = async (): Promise<void> => {
    if (!token) return;
    setSubmitting(true);
    try {
      await confirmOrder(token, { status: 'accepted' });
      setData(prev => prev ? ({ ...prev, order: { ...prev.order, status: 'accepted' as const } }) : null);
      setActionPending(null);
      toast.success('Order accepterad');
    } catch (error) {
      toast.error('Ett fel uppstod');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async (): Promise<void> => {
    if (!token) return;
    if (!actionPending && !window.confirm('Är du säker på att du vill avvisa ordern?')) return;
    
    setSubmitting(true);
    try {
      await confirmOrder(token, { status: 'rejected' });
      setData(prev => prev ? ({ ...prev, order: { ...prev.order, status: 'rejected' as const } }) : null);
      setActionPending(null);
      toast.success('Order avvisad');
    } catch (error) {
      toast.error('Ett fel uppstod');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendMessage = async (): Promise<void> => {
    if (!messageText.trim() || !token) return;
    
    setSubmitting(true);
    try {
      const { data: newMessage } = await sendOrderMessage(token, { text: messageText });
      setData(prev => prev ? ({
        ...prev,
        order: {
          ...prev.order,
          supplier_messages: [...(prev.order?.supplier_messages || []), newMessage]
        }
      }) : null);
      setMessageText('');
      toast.success('Meddelande skickat');
    } catch (error) {
      toast.error('Kunde inte skicka meddelande');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitQuote = async (): Promise<void> => {
    if (!quoteForm.amount || !token) {
      toast.error('Ange offertbelopp');
      return;
    }
    
    setSubmitting(true);
    try {
      await submitOrderQuote(token, {
        amount: parseFloat(quoteForm.amount),
        description: quoteForm.description,
        attachments: quoteForm.attachments
      });
      setData(prev => prev ? ({
        ...prev,
        order: {
          ...prev.order,
          status: 'waiting_quote_approval' as const,
          quote_amount: parseFloat(quoteForm.amount),
          quote_description: quoteForm.description
        }
      }) : null);
      toast.success('Offert inskickad');
      setActiveTab('order');
    } catch (error) {
      toast.error('Kunde inte skicka offert');
    } finally {
      setSubmitting(false);
    }
  };

  const handleComplete = async (): Promise<void> => {
    if (!token) return;
    if (!completionForm.notes) {
      toast.error('Beskriv utfört arbete');
      return;
    }
    
    setSubmitting(true);
    try {
      await completeOrder(token, {
        notes: completionForm.notes,
        attachments: completionForm.attachments
      });
      const newStatus = data?.order?.quote_amount ? 'waiting_invoice' : 'completed';
      setData(prev => prev ? ({
        ...prev,
        order: {
          ...prev.order,
          status: newStatus as 'waiting_invoice' | 'completed',
          completion_notes: completionForm.notes
        }
      }) : null);
      toast.success('Arbete rapporterat');
      setActiveTab('order');
    } catch (error) {
      toast.error('Kunde inte slutföra');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'sv' ? 'en' : 'sv';
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  type OrderStatus = 'pending' | 'accepted' | 'rejected' | 'waiting_quote_approval' | 'waiting_invoice' | 'completed' | 'confirmed';
  
  const getStatusConfig = (status: OrderStatus | undefined): { label: string; bg: string; dot: string } => {
    const configs: Record<OrderStatus, { label: string; bg: string; dot: string }> = {
      pending: { label: 'Väntande', bg: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' },
      accepted: { label: 'Accepterad', bg: 'bg-emerald-100 text-emerald-800', dot: 'bg-emerald-500' },
      rejected: { label: 'Avvisad', bg: 'bg-red-100 text-red-800', dot: 'bg-red-500' },
      waiting_quote_approval: { label: 'Väntar på godkänd offert', bg: 'bg-red-100 text-red-800', dot: 'bg-red-500' },
      waiting_invoice: { label: 'Väntar på faktura', bg: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' },
      completed: { label: 'Slutförd', bg: 'bg-emerald-100 text-emerald-800', dot: 'bg-emerald-500' },
      confirmed: { label: 'Bekräftad', bg: 'bg-blue-100 text-blue-800', dot: 'bg-blue-500' }
    };
    return configs[status || 'pending'] || configs.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-400">Laddar...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <XCircle size={48} className="mx-auto text-red-400 mb-4" />
          <h1 className="text-lg font-semibold text-slate-800">Order hittades inte</h1>
          <p className="text-sm text-slate-500 mt-1">Länken kan ha gått ut eller vara felaktig</p>
        </div>
      </div>
    );
  }

  const { order, issue } = data;
  const statusConfig = getStatusConfig(order.status);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="order-confirm-page">
      {/* Action confirmation banner from email link */}
      {actionPending && order.status === 'pending' && (
        <div className={`${actionPending === 'accept' ? 'bg-emerald-500' : 'bg-red-500'} text-white py-4`}>
          <div className="max-w-2xl mx-auto px-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">
                  {actionPending === 'accept' 
                    ? 'Du har valt att acceptera denna order' 
                    : 'Du har valt att avvisa denna order'}
                </p>
                <p className="text-sm opacity-90">Bekräfta ditt val nedan</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setActionPending(null)}
                  className="bg-white/20 hover:bg-white/30 text-white border-0"
                >
                  Avbryt
                </Button>
                <Button
                  size="sm"
                  onClick={actionPending === 'accept' ? handleAccept : handleReject}
                  disabled={submitting}
                  className="bg-white text-slate-900 hover:bg-white/90"
                >
                  {submitting ? 'Skickar...' : (actionPending === 'accept' ? 'Bekräfta acceptans' : 'Bekräfta avvisning')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wrench size={24} className="text-slate-700" />
            <h1 className="text-lg font-semibold text-slate-800">Orderhantering</h1>
          </div>
          <button
            onClick={toggleLanguage}
            className="text-sm text-slate-500 hover:text-slate-700"
          >
            {i18n.language === 'sv' ? 'EN' : 'SV'}
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Status Badge */}
        <div className="mb-6">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${statusConfig.bg}`}>
            <div className={`w-2 h-2 rounded-full ${statusConfig.dot}`}></div>
            <span className="text-sm font-medium">{statusConfig.label}</span>
          </div>
        </div>

        {/* Action buttons for pending status */}
        {order.status === 'pending' && !actionPending && (
          <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6">
            <h2 className="text-sm font-medium text-slate-700 mb-3">Svara på order</h2>
            <div className="flex gap-3">
              <Button
                onClick={handleAccept}
                disabled={submitting}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                data-testid="accept-order-button"
              >
                <CheckCircle size={18} className="mr-2" />
                Acceptera
              </Button>
              <Button
                onClick={handleReject}
                disabled={submitting}
                variant="destructive"
                className="flex-1"
                data-testid="reject-order-button"
              >
                <XCircle size={18} className="mr-2" />
                Avvisa
              </Button>
            </div>
          </div>
        )}

        {/* Tabs for accepted orders */}
        {order.status && ['accepted', 'waiting_quote_approval'].includes(order.status) && (
          <div className="flex gap-1 p-1 bg-slate-100 rounded-lg mb-6">
            <button
              onClick={() => setActiveTab('order')}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'order' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              Orderinfo
            </button>
            <button
              onClick={() => setActiveTab('quote')}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'quote' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              Lämna offert
            </button>
            <button
              onClick={() => setActiveTab('complete')}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'complete' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              Slutför
            </button>
          </div>
        )}

        {/* Quote form */}
        {activeTab === 'quote' && order.status && ['accepted', 'waiting_quote_approval'].includes(order.status) && (
          <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6">
            <h2 className="text-sm font-medium text-slate-700 mb-4 flex items-center gap-2">
              <Receipt size={18} />
              Lämna offert
            </h2>
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Belopp (kr) *</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={quoteForm.amount}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, amount: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Beskrivning</Label>
                <Textarea
                  placeholder="Beskriv offerten..."
                  value={quoteForm.description}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, description: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Bilagor</Label>
                <FileUpload
                  onUpload={(urls) => setQuoteForm(prev => ({ ...prev, attachments: [...prev.attachments, ...urls] }))}
                  className="mt-1"
                />
              </div>
              <Button onClick={handleSubmitQuote} disabled={submitting} className="w-full">
                {submitting ? 'Skickar...' : 'Skicka offert'}
              </Button>
            </div>
          </div>
        )}

        {/* Completion form */}
        {activeTab === 'complete' && order.status && ['accepted', 'waiting_quote_approval'].includes(order.status) && (
          <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6">
            <h2 className="text-sm font-medium text-slate-700 mb-4 flex items-center gap-2">
              <CheckCircle size={18} />
              Rapportera slutfört arbete
            </h2>
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Beskrivning av utfört arbete *</Label>
                <Textarea
                  placeholder="Beskriv vad som har gjorts..."
                  value={completionForm.notes}
                  onChange={(e) => setCompletionForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="mt-1 min-h-[120px]"
                />
              </div>
              <div>
                <Label className="text-xs">Bilagor (bilder etc)</Label>
                <FileUpload
                  onUpload={(urls) => setCompletionForm(prev => ({ ...prev, attachments: [...prev.attachments, ...urls] }))}
                  className="mt-1"
                />
              </div>
              <Button onClick={handleComplete} disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700">
                {submitting ? 'Skickar...' : 'Markera som slutfört'}
              </Button>
            </div>
          </div>
        )}

        {/* Order details (always visible when tab = order) */}
        {(activeTab === 'order' || !order.status || !['accepted', 'waiting_quote_approval'].includes(order.status)) && (
          <>
            {/* Issue info */}
            <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6">
              <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white p-4 rounded-lg mb-4">
                <div className="text-xs opacity-80 mb-1">{issue?.issue_number || 'Ärende'}</div>
                <h3 className="font-semibold">{issue?.title}</h3>
                <div className="flex gap-2 mt-2">
                  <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">{issue?.category}</span>
                </div>
              </div>

              {/* Description */}
              {issue?.description && (
                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Ärendebeskrivning</div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{issue.description}</p>
                </div>
              )}

              {/* Conversation */}
              {(order.email_thread?.messages?.length ?? 0) > 0 && (
                <div className="mb-4">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Konversation</div>
                  <div className="space-y-3">
                    {order.email_thread?.messages
                      ?.filter((msg: EmailMessage, idx: number) => {
                        if (idx === 0 && issue?.description) {
                          const descClean = issue.description.trim().substring(0, 100);
                          const bodyClean = (msg.body || '').trim().substring(0, 100);
                          return descClean !== bodyClean;
                        }
                        return true;
                      })
                      .map((msg: EmailMessage, idx: number) => {
                        const isOutbound = msg.direction === 'outbound';
                        return (
                          <div key={idx} className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[80%] p-3 rounded-2xl ${
                              isOutbound 
                                ? 'bg-slate-800 text-white rounded-br-sm' 
                                : 'bg-white border border-slate-200 rounded-bl-sm shadow-sm'
                            }`}>
                              <div className={`text-xs mb-1 ${isOutbound ? 'opacity-70' : 'text-slate-500'}`}>
                                {msg.from_email?.split('@')[0]} • {new Date(msg.created_at).toLocaleString('sv-SE')}
                              </div>
                              <div className="text-sm whitespace-pre-wrap">{msg.body}</div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Internal notes (GREEN) */}
              {(issue?.internal_notes?.length ?? 0) > 0 && (
                <div className="mb-4">
                  <div className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-3">Interna anteckningar</div>
                  <div className="space-y-3">
                    {issue?.internal_notes
                      ?.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                      .map((note, idx) => (
                        <div key={idx} className="flex justify-start">
                          <div className="max-w-[80%] p-3 rounded-2xl bg-emerald-100 border border-emerald-200 rounded-bl-sm">
                            <div className="text-xs text-emerald-600 mb-1">
                              {note.author_name || 'Intern'} • {new Date(note.created_at).toLocaleString('sv-SE')}
                            </div>
                            <div className="text-sm text-emerald-900 whitespace-pre-wrap">{note.text}</div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Supplier messages (YELLOW) */}
              {(order.supplier_messages?.length ?? 0) > 0 && (
                <div className="mb-4">
                  <div className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-3">Dina meddelanden</div>
                  <div className="space-y-3">
                    {order.supplier_messages?.map((msg, idx) => (
                      <div key={idx} className="flex justify-start">
                        <div className="max-w-[80%] p-3 rounded-2xl bg-amber-100 border border-amber-200 rounded-bl-sm">
                          <div className="text-xs text-amber-600 mb-1">
                            Du • {new Date(msg.created_at).toLocaleString('sv-SE')}
                          </div>
                          <div className="text-sm text-amber-900 whitespace-pre-wrap">{msg.text}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Reporter contact */}
              {(issue?.reporter_name || issue?.reporter_email || issue?.reporter_phone) && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-2">Kontaktuppgifter</div>
                  <div className="text-sm text-emerald-700 space-y-1">
                    {issue.reporter_name && <div><strong>Namn:</strong> {issue.reporter_name}</div>}
                    {issue.reporter_email && <div><strong>E-post:</strong> {issue.reporter_email}</div>}
                    {issue.reporter_phone && <div><strong>Telefon:</strong> {issue.reporter_phone}</div>}
                  </div>
                </div>
              )}
            </div>

            {/* Message input for accepted orders */}
            {order.status && ['accepted', 'waiting_quote_approval', 'waiting_invoice'].includes(order.status) && (
              <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6">
                <h2 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <ChatText size={18} />
                  Skicka meddelande
                </h2>
                <div className="flex gap-2">
                  <Textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Skriv ett meddelande till förvaltningen..."
                    className="min-h-[60px] text-sm"
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={!messageText.trim() || submitting}
                    size="sm"
                    className="self-end"
                  >
                    <PaperPlaneTilt size={16} />
                  </Button>
                </div>
              </div>
            )}

            {/* Quote info if submitted */}
            {order.quote_amount && (
              <div className={`rounded-lg border p-4 mb-6 ${
                order.status === 'waiting_quote_approval' 
                  ? 'bg-red-50 border-red-200' 
                  : order.status === 'confirmed'
                    ? 'bg-emerald-50 border-emerald-200' 
                    : 'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <Receipt size={18} className={order.status === 'waiting_quote_approval' ? 'text-red-600' : 'text-slate-600'} />
                  <span className="text-sm font-medium">
                    {order.status === 'waiting_quote_approval' ? 'Offert inväntar godkännande' : 'Offert'}
                  </span>
                </div>
                <div className="text-2xl font-bold text-slate-800">{order.quote_amount.toLocaleString('sv-SE')} kr</div>
                {order.quote_description && (
                  <p className="text-sm text-slate-600 mt-2">{order.quote_description}</p>
                )}
              </div>
            )}

            {/* Completion info */}
            {order.completion_notes && (
              <div className={`rounded-lg border p-4 mb-6 ${
                order.status === 'waiting_invoice' 
                  ? 'bg-amber-50 border-amber-200' 
                  : 'bg-emerald-50 border-emerald-200'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle size={18} className={order.status === 'waiting_invoice' ? 'text-amber-600' : 'text-emerald-600'} />
                  <span className="text-sm font-medium">
                    {order.status === 'waiting_invoice' ? 'Arbete klart - Inväntar faktura' : 'Slutfört arbete'}
                  </span>
                </div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{order.completion_notes}</p>
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="max-w-2xl mx-auto px-4 py-6 text-center text-xs text-slate-400">
        Förvaltningen
      </footer>
    </div>
  );
}
